import React from "react";
import "./FichaPrevia.css";
import {
  quitarAcentos,
  validarNombreLongitud,
  validarNombreEspacios,
  validarNombreCaracteres,
  normalizarCURP,
  validarLongitudCURP,
  validarDigitoVerificador,
  esCurpExtranjero,
  validarFechaCURP,
  validarSigloCURPContraFechaNacimiento,
  validarFechaNacimientoMinima,
  validarFechaNacimientoNoFutura,
} from "../../utils/validaciones/identidad";
import {
  msgNombreLongitud, msgNombreEspacios, msgNombreCaracteres,
  msgApellidoPaternoLongitud, msgApellidoPaternoEspacios, msgApellidoPaternoCaracteres,
  msgApellidoMaternoLongitud, msgApellidoMaternoEspacios, msgApellidoMaternoCaracteres,
  msgLongitudCURP, msgDigitoVerificador, msgFechaCURP, msgSigloCURPFechaNacimiento,
  msgFechaNacimientoMinima, msgFechaNacimientoNoFutura,
} from "../../utils/validaciones/mensajes";
import { toast } from "react-toastify";

/**
 * Formulario de datos del paciente (PÚBLICO).
 *
 * Lo usan:
 * - FichaPrevia (link que llega por WhatsApp)
 * - AgendaPublica (página pública de agendamiento)
 *
 * Solo pinta y valida. Quien lo usa guarda el estado y decide a dónde mandar los datos.
 * Mismas validaciones que Alta Paciente (sin CURP vs prestador ni catálogos:
 * eso lo revisa el médico al confirmar el alta).
 */

const ETIQUETAS = {
  nombre: "Nombre(s)",
  apellido_paterno: "Apellido paterno",
  apellido_materno: "Apellido materno",
  fecha_nacimiento: "Fecha de nacimiento",
  sexo: "Sexo",
  curp: "CURP",
  telefono: "Teléfono celular (10 dígitos)",
  correo: "Correo electrónico",
  motivo: "Motivo de tu consulta",
};

// Opciones de motivo (lo que se guarda es el texto tal cual).
// Para agregar más, solo agrega un renglón aquí.
const OPCIONES_MOTIVO = ["Consulta presencial", "Seguimiento de tratamiento", "Otro"];
// Por ahora el motivo NO se le pregunta al paciente: se manda fijo.
// Para mostrar la lista, cambiar a true.
const MOSTRAR_MOTIVO = false;
const MOTIVO_POR_DEFECTO = "Consulta";

// Igual que en Alta: estos van con *
const OBLIGATORIOS = ["nombre", "apellido_paterno", "fecha_nacimiento", "sexo", 
  ...(MOSTRAR_MOTIVO ? ["motivo"] : []),
];

const CAMPOS_RESPONSABLE = [
  "responsable_nombre",
  "responsable_apellido_paterno",
  "responsable_apellido_materno",
];

// Validaciones de nombre: mismas funciones y mensajes que Alta
const VALIDADORES_NOMBRE = {
  nombre: [msgNombreLongitud, msgNombreEspacios, msgNombreCaracteres],
  apellido_paterno: [msgApellidoPaternoLongitud, msgApellidoPaternoEspacios, msgApellidoPaternoCaracteres],
  apellido_materno: [msgApellidoMaternoLongitud, msgApellidoMaternoEspacios, msgApellidoMaternoCaracteres],
  responsable_nombre: [msgNombreLongitud, msgNombreEspacios, msgNombreCaracteres],
  responsable_apellido_paterno: [msgApellidoPaternoLongitud, msgApellidoPaternoEspacios, msgApellidoPaternoCaracteres],
  responsable_apellido_materno: [msgApellidoMaternoLongitud, msgApellidoMaternoEspacios, msgApellidoMaternoCaracteres],
};

const nombreValido = (campo, valor) => {
  if (!valor) return true; // vacío se revisa aparte (obligatorios)
  const [msgLongitud, msgEspacios, msgCaracteres] = VALIDADORES_NOMBRE[campo];
  if (!validarNombreLongitud(valor)) { msgLongitud(); return false; }
  if (!validarNombreEspacios(valor)) { msgEspacios(); return false; }
  if (!validarNombreCaracteres(valor)) { msgCaracteres(); return false; }
  return true;
};

const curpValida = (curp, fechaNacimiento) => {
  if (!curp) return true; // opcional
  if (!validarLongitudCURP(curp)) { msgLongitudCURP(); return false; }
  if (!validarDigitoVerificador(curp)) { msgDigitoVerificador(); return false; }
  if (esCurpExtranjero(curp)) return true;
  if (!validarFechaCURP(curp)) { msgFechaCURP(); return false; }
  if (fechaNacimiento && !validarSigloCURPContraFechaNacimiento(curp, fechaNacimiento)) {
    msgSigloCURPFechaNacimiento();
    return false;
  }
  return true;
};

// =========================
// Validar todo (se llama al dar "Enviar" / "Agendar")
// =========================
export const validarDatosPaciente = ({
  campos,
  datos,
  esParaMi,
  avisoAceptado,
  requiereContacto = false, // página pública: al menos teléfono o correo
}) => {
  const pide = (campo) => campos.includes(campo);

  for (const campo of OBLIGATORIOS) {
    if (pide(campo) && !String(datos[campo] || "").trim()) {
      toast.error(`Falta: ${ETIQUETAS[campo]}`);
      return false;
    }
  }

  for (const campo of ["nombre", "apellido_paterno", "apellido_materno"]) {
    if (pide(campo) && !nombreValido(campo, datos[campo])) return false;
  }

  if (datos.fecha_nacimiento) {
    if (!validarFechaNacimientoMinima(datos.fecha_nacimiento)) { msgFechaNacimientoMinima(); return false; }
    if (!validarFechaNacimientoNoFutura(datos.fecha_nacimiento)) { msgFechaNacimientoNoFutura(); return false; }
  }

  if (datos.telefono && !/^\d{10}$/.test(datos.telefono)) {
    toast.error("El teléfono debe tener exactamente 10 dígitos.");
    return false;
  }

  if (requiereContacto && !String(datos.telefono || "").trim() && !String(datos.correo || "").trim()) {
    toast.error("Captura al menos un teléfono o un correo para poder contactarte.");
    return false;
  }

  if (datos.curp) {
    const curp = normalizarCURP(datos.curp);
    if (!curpValida(curp, datos.fecha_nacimiento)) return false;
  }

  if (!esParaMi) {
    if (!String(datos.responsable_nombre || "").trim()) {
      toast.error("Escribe el nombre de quien llena el formulario.");
      return false;
    }
    for (const campo of CAMPOS_RESPONSABLE) {
      if (!nombreValido(campo, datos[campo])) return false;
    }
  }

  if (!avisoAceptado) {
    toast.error("Para continuar debes aceptar el aviso de privacidad.");
    return false;
  }

  return true;
};

// =========================
// Armar lo que se manda al backend
// =========================
export const armarPayloadPaciente = ({ campos, datos, esParaMi }) => {
  const payload = {};

  campos.forEach((campo) => {
    const valor = datos[campo];
    if (valor !== undefined && String(valor).trim() !== "") {
      payload[campo] = campo === "curp" ? normalizarCURP(valor) : String(valor).trim();
    }
  });

  // Motivo escondido: si el backend lo pide, va fijo
  if (campos.includes("motivo") && !payload.motivo) {
    payload.motivo = MOTIVO_POR_DEFECTO;
  }

  payload.es_para_mi = esParaMi;
  if (!esParaMi) {
    CAMPOS_RESPONSABLE.forEach((campo) => {
      if (datos[campo]) payload[campo] = datos[campo].trim();
    });
  }
  payload.aviso_privacidad_aceptado = true;

  return payload;
};

// =========================
// El formulario
// =========================
const FormularioDatosPaciente = ({
  campos,
  datos,
  setDatos,
  esParaMi,
  setEsParaMi,
  avisoTexto,
  avisoUrl,
  avisoAceptado,
  setAvisoAceptado,
  erroresServidor = {},
}) => {
  const pide = (campo) => campos.includes(campo);

  const cambiar = (campo, valor) => {
    setDatos((prev) => ({ ...prev, [campo]: valor }));
  };

  const blurNombre = (campo) => {
    const v = quitarAcentos((datos[campo] || "").trim());
    cambiar(campo, nombreValido(campo, v) ? v : "");
  };

  const errorDe = (campo) => {
    const e = erroresServidor?.[campo];
    if (!e) return null;
    return <div className="ficha-error-campo">{Array.isArray(e) ? e.join(" ") : String(e)}</div>;
  };

  const inputNombre = (campo, etiqueta) => (
    <label className="ficha-campo" key={campo}>
      <span>{etiqueta}</span>
      <input
        type="text"
        maxLength={50}
        value={datos[campo] || ""}
        onChange={(e) => cambiar(campo, e.target.value)}
        onBlur={() => blurNombre(campo)}
      />
      {errorDe(campo)}
    </label>
  );

  const etiquetaPaciente = (campo) =>
    `${ETIQUETAS[campo]}${OBLIGATORIOS.includes(campo) ? " *" : ""}`;

  return (
    <>
      {/* ¿Para quién es? */}
      <div className="ficha-para-quien">
        <span>¿Es para ti?</span>
        <label>
          <input type="radio" checked={esParaMi} onChange={() => setEsParaMi(true)} /> Sí, es para mí
        </label>
        <label>
          <input type="radio" checked={!esParaMi} onChange={() => setEsParaMi(false)} /> No, es para otra persona
        </label>
      </div>

      {!esParaMi && (
        <div className="ficha-bloque">
          <p className="ficha-bloque-titulo">Tus datos (quien llena el formulario)</p>
          {inputNombre("responsable_nombre", "Nombre(s) *")}
          {inputNombre("responsable_apellido_paterno", "Apellido paterno")}
          {inputNombre("responsable_apellido_materno", "Apellido materno")}
          <p className="ficha-bloque-titulo">Datos del paciente</p>
        </div>
      )}

      {pide("nombre") && inputNombre("nombre", etiquetaPaciente("nombre"))}
      {pide("apellido_paterno") && inputNombre("apellido_paterno", etiquetaPaciente("apellido_paterno"))}
      {pide("apellido_materno") && inputNombre("apellido_materno", etiquetaPaciente("apellido_materno"))}

      {pide("fecha_nacimiento") && (
        <label className="ficha-campo">
          <span>{etiquetaPaciente("fecha_nacimiento")}</span>
          <input
            type="date"
            value={datos.fecha_nacimiento || ""}
            onChange={(e) => cambiar("fecha_nacimiento", e.target.value)}
          />
          {errorDe("fecha_nacimiento")}
        </label>
      )}

      {pide("sexo") && (
        <label className="ficha-campo">
          <span>{etiquetaPaciente("sexo")}</span>
          <select value={datos.sexo || ""} onChange={(e) => cambiar("sexo", e.target.value)}>
            <option value="">Selecciona…</option>
            <option value="M">Hombre</option>
            <option value="F">Mujer</option>
            <option value="O">Intersexual</option>
          </select>
          {errorDe("sexo")}
        </label>
      )}

      {pide("curp") && (
        <label className="ficha-campo">
          <span>{ETIQUETAS.curp} (opcional)</span>
          <input
            type="text"
            maxLength={18}
            value={datos.curp || ""}
            onChange={(e) => cambiar("curp", e.target.value.toUpperCase())}
            onBlur={() => {
              const curp = normalizarCURP(datos.curp || "");
              cambiar("curp", curp);
              curpValida(curp, datos.fecha_nacimiento);
            }}
          />
          {errorDe("curp")}
        </label>
      )}

      {pide("telefono") && (
        <label className="ficha-campo">
          <span>{ETIQUETAS.telefono}</span>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            value={datos.telefono || ""}
            onChange={(e) => cambiar("telefono", e.target.value.replace(/\D/g, ""))}
          />
          {errorDe("telefono")}
        </label>
      )}

      {pide("correo") && (
        <label className="ficha-campo">
          <span>{ETIQUETAS.correo}</span>
          <input
            type="email"
            maxLength={100}
            value={datos.correo || ""}
            onChange={(e) => cambiar("correo", e.target.value.trim())}
          />
          {errorDe("correo")}
        </label>
      )}

          
      {MOSTRAR_MOTIVO && pide("motivo") && (
        <label className="ficha-campo">
          <span>{etiquetaPaciente("motivo")}</span>
          <select value={datos.motivo || ""} onChange={(e) => cambiar("motivo", e.target.value)}>
            <option value="">Selecciona…</option>
            {OPCIONES_MOTIVO.map((opcion) => (
              <option key={opcion} value={opcion}>{opcion}</option>
            ))}
          </select>
          {errorDe("motivo")}
        </label>
      )}

           {/* Aviso de privacidad */}
      <div className="ficha-aviso">
        <p>{avisoTexto}</p>
        <label>
          <input
            type="checkbox"
            checked={avisoAceptado}
            onChange={(e) => setAvisoAceptado(e.target.checked)}
          />{" "}
          He leído y acepto el{" "}
          {avisoUrl ? (
            <a
              href={avisoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ficha-aviso-link"
            >
              aviso de privacidad
            </a>
          ) : (
            "aviso de privacidad"
          )}
          .
        </label>
        {errorDe("aviso_privacidad_aceptado")}
      </div>

      {errorDe("non_field_errors")}
    </>
  );
};

export default FormularioDatosPaciente;